import { FormAtom } from "@impero-com/common/utils/form/formAtom/types";
import { SetStateAction, WritableAtom } from "jotai";
import { focusAtom } from "jotai-optics";

import {
    NotificationType,
    InitialAssignmentKind,
    SubsequentAssignmentKind,
    NotificationRecipientKind,
} from "api/generated/model";
import { Guid } from "utils/guid";

export type AssignmentGroupId = Guid;

export interface ControlAssignmentData {
    initialAssignment: Record<AssignmentGroupId, InitialAssignmentKind>; // data for the "responsible" column, keyed by assignment group id
    subsequentAssignments: Array<
        Record<AssignmentGroupId, SubsequentAssignmentKind>
    >; // data for the "first reviewer" / "second reviewer" column, keyed by assignment group id
    tagIds: Record<AssignmentGroupId, Guid[]>;
    loginProtectionEnabled: boolean;
    segregationOfDutiesEnabled: boolean;
    notificationRecipients: Record<
        AssignmentGroupId,
        Partial<Record<NotificationType, NotificationRecipientKind[]>>
    >;
    entityIds: Record<AssignmentGroupId, number>;
}

// TODO(jg): We need to update this interface to use an array for subsequent assignments instead of first and second reviewer when we use more than two steps
export interface ControlAssignmentGroup {
    id: AssignmentGroupId;
    initialAssignment: InitialAssignmentKind;
    firstReviewer?: SubsequentAssignmentKind;
    secondReviewer?: SubsequentAssignmentKind;
    tagIds?: Guid[];
    notificationRecipients?: Partial<
        Record<NotificationType, NotificationRecipientKind[]>
    >;
    entityId?: number;
}

export function controlAssignmentDataToControlAssignmentGroups(
    data: ControlAssignmentData
): ControlAssignmentGroup[] {
    return Object.entries(data.initialAssignment).map(
        ([assignmentGroupId, initialAssignment]) => {
            return {
                id: assignmentGroupId,
                initialAssignment,
                firstReviewer: data.subsequentAssignments[0][assignmentGroupId],
                secondReviewer:
                    data.subsequentAssignments[1][assignmentGroupId],
                tagIds: data.tagIds[assignmentGroupId],
                notificationRecipients:
                    data.notificationRecipients[assignmentGroupId],
                entityId: data.entityIds[assignmentGroupId],
            };
        }
    );
}

export function updateControlAssignmentData(
    data: ControlAssignmentData,
    update: ControlAssignmentGroup[]
): ControlAssignmentData {
    const initialAssignment: ControlAssignmentData["initialAssignment"] = {};
    const subsequentAssignments: ControlAssignmentData["subsequentAssignments"] =
        [{}, {}];
    const tagIds: ControlAssignmentData["tagIds"] = {};
    const notificationRecipients: ControlAssignmentData["notificationRecipients"] =
        {};
    const entityIds: ControlAssignmentData["entityIds"] = {};
    for (const group of update) {
        initialAssignment[group.id] = group.initialAssignment;
        if (group.firstReviewer) {
            subsequentAssignments[0][group.id] = group.firstReviewer;
        }
        if (group.secondReviewer) {
            subsequentAssignments[1][group.id] = group.secondReviewer;
        }
        if (group.tagIds) {
            tagIds[group.id] = group.tagIds;
        }
        if (group.notificationRecipients) {
            notificationRecipients[group.id] = group.notificationRecipients;
        }
        if (group.entityId) {
            entityIds[group.id] = group.entityId;
        }
    }

    return {
        ...data,
        initialAssignment,
        subsequentAssignments,
        tagIds,
        notificationRecipients,
        entityIds,
    };
}

export function createControlAssignmentGroupAtom(
    controlAssignmentAtom: FormAtom<ControlAssignmentData>
): WritableAtom<
    ControlAssignmentGroup[],
    [SetStateAction<ControlAssignmentGroup[]>],
    Promise<void>
> {
    return focusAtom(controlAssignmentAtom, (o) =>
        o.lens(
            controlAssignmentDataToControlAssignmentGroups,
            updateControlAssignmentData
        )
    );
}
